import { endTracking, startTracking } from '@account/account-portal/config'
import {
  loginUser,
  startPasswordReset,
  Touchpoint,
  useLoginStatus,
  usePasswordResetStartStatus,
} from '@account/account-portal/lib'
import { LoginCredentials, LoginError, LoginOption } from '@account/account-portal/types'
import { Consents } from '@account/consents'
import { GlButton } from '@adl/component-button'
import { GlForm } from '@adl/component-form'
import { GlInput } from '@adl/component-input'
import { GlLink } from '@adl/component-link'
import { GlTooltip } from '@adl/component-tooltip'
import { useConfig } from '@crystal/config'
import { useMfProps, useDevice } from '@crystal/core'
import { useFeatureFlags } from '@crystal/feature-flags'
import { SubmitHandler, useFormValidation } from '@crystal/form'
import { useTranslations, Translations } from '@crystal/translator'
import { useEffect, useState } from 'react'

import { AccountPortalConfig } from '../config'
import { useViewData, useIsOneStepView, ScreenViews } from '../lib'
import {
  trackLoginStart,
  trackLoginSuccess,
  trackForgetPwStart,
  trackForgetPwSuccess,
  trackLoginError,
  trackFakeDoorOTP,
} from '../lib/analytics'

import styles from './account-portal.module.scss'
import { KmcButton } from './KmcButton'
import { FormContainer } from './layout/FormContainer'
import { PasswordInput } from './PasswordInput'
import { PersistentLoginCheckbox } from './PersistentLoginCheckbox'
import { SocialLogin } from './SocialLogin'
import { SuggestSocialLoginOption } from './SuggestSocialLoginOption'

const FAKEDOOR_SURVEY_URL = 'https://feedback.adidas.com/jfe/form/SV_2isCMh0mdetjqGq'

export const Login = () => {
  const t = useTranslations<Translations>()
  const { OTP_FAKEDOOR_ENABLED } = useFeatureFlags()
  const { registerField, handleSubmit, formState, setError, getValues } = useFormValidation<LoginCredentials>()
  const { emailPolicy, passwordPolicyLogin, isKmcFlowRequired } = useConfig<AccountPortalConfig>()
  const [generalError, setGeneralError] = useState<string>()
  const { isPending: isLoginPending } = useLoginStatus()
  const { isPending: isPasswordResetPending } = usePasswordResetStartStatus()
  const isPending = isLoginPending || isPasswordResetPending
  const { viewData, updateViewData, showScreen } = useViewData()
  const {
    email: prefilledEmail,
    persistentLogin,
    showPersistentLoginCheckbox: showPersistentLogin,
    loginMethod,
    preset,
  } = viewData
  const isCheckout = preset === 'checkout-inline'
  const showPersistentLoginCheckbox = showPersistentLogin && !isCheckout
  const mfProps = useMfProps()
  const { isMobile } = useDevice()
  const email = getValues('user') || prefilledEmail || ''
  const isSocialMethodEnabled = (preset === 'login-method' || isCheckout) && loginMethod === LoginOption.Social
  const isOneStepView = useIsOneStepView(viewData)
  const shouldShowFakeDoor =
    OTP_FAKEDOOR_ENABLED && viewData.touchpoint === Touchpoint.BehaviouralOverlay && !isOneStepView

  useEffect(() => {
    endTracking('email_to_password_transition')
  }, [])

  useEffect(() => {
    if (!showPersistentLoginCheckbox) {
      return
    }
    updateViewData({
      persistentLogin: getValues().persistentLogin,
    })
  }, [getValues().persistentLogin])

  function showApiErrors({ validationErrors, details, isKMCRequired }: LoginError) {
    if (details?.statusCode === 400 && isKMCRequired) {
      return setError('password', { message: t('login.error.kmcRequired') })
    }
    if (details?.statusCode === 400 && validationErrors?.error === 'invalid_grant') {
      setGeneralError('')
      setError('password', { message: t('login.error') })
    } else if (details?.statusCode === 401 && validationErrors) {
      setGeneralError('')
      setError('password', { message: t('password.error.min.age', t('global.country')) })
    } else {
      setGeneralError(t('unknownError.message'))
    }
  }

  const onSubmit: SubmitHandler<LoginCredentials> = async ({ password }) => {
    const credentials: LoginCredentials = {
      user: email,
      password,
      persistentLogin,
    }
    startTracking('password_to_account_overlay_transition')
    await loginUser(credentials)
      .then(() => {
        trackLoginSuccess()
      })
      .catch(err => {
        showApiErrors(err as LoginError)
        trackLoginError()
      })
      .finally(() => {
        endTracking('password_to_account_overlay_transition')
      })
  }

  const onPasswordReset = async () => {
    if (!email) {
      // Triggers form validation
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      handleSubmit(() => {})()
      return
    }
    trackForgetPwStart()
    await startPasswordReset({ email })
      .then(() => showScreen(ScreenViews.RESET_PASSWORD_CONFIRMATION_SCREEN))
      .then(() => trackForgetPwSuccess())
      .catch(() => setGeneralError(t('unknownError.message')))
  }

  const isFocusedFirstTime = (fieldName: string) => {
    return !formState.touchedFields?.[fieldName as keyof LoginCredentials]
  }

  const onFocus = () => {
    if (isFocusedFirstTime('password')) {
      trackLoginStart()
    }
  }

  return (
    <FormContainer>
      <h1 className={styles['title']}>{t('login.title')}</h1>
      {isCheckout && <p className={styles['form-title']}>{t('landing.chk.title')}</p>}
      <p className={styles['subtitle']}>{t('login.subtitle')}</p>
      {isSocialMethodEnabled ? (
        <SocialLogin persistentLogin={viewData.persistentLogin} />
      ) : (
        <GlForm noValidate onSubmit={handleSubmit(onSubmit)}>
          {prefilledEmail ? (
            <SuggestSocialLoginOption email={prefilledEmail} />
          ) : (
            <>
              <SocialLogin persistentLogin={viewData.persistentLogin} />
              <div className="gl-vspace-bpall-small">{t('or.separator')}</div>
            </>
          )}
          {!prefilledEmail && (
            <GlInput
              id="email"
              type="email"
              autoComplete="email"
              className="gl-form-item gl-vspace-bpall-small"
              labelText={t('email.label')}
              aria-label={t('email.label')}
              {...registerField('user', {
                ...emailPolicy,
                errorMessage: t('email.error'),
              })}
            />
          )}
          <PasswordInput
            id="password"
            className="gl-form-item"
            labelText={t('password.label')}
            aria-label={t('password.label')}
            onFocusCapture={() => onFocus()}
            {...registerField('password', {
              ...(email ? passwordPolicyLogin : {}),
              errorMessage: email ? t('password.hint') : t('password.error'),
            })}
          />
          <GlButton
            fullWidth={isMobile}
            type="submit"
            className="gl-vspace-bpall-small"
            aria-label={t('registerButton.label')}
            loading={isLoginPending}
            disabled={isPending}
            id="login-submit-button">
            {t('loginButton.label')}
          </GlButton>

          {shouldShowFakeDoor && (
            <GlTooltip
              className={styles['fake-door-test-tooltip']}
              closeButtonEnabled={true}
              onOpen={trackFakeDoorOTP}
              trigger={({ ref, toggleTooltip }) => (
                <GlButton
                  fullWidth={isMobile}
                  variant="secondary"
                  ref={ref}
                  onClick={toggleTooltip}
                  className="gl-vspace-bpall-small"
                  aria-label={t('login.fakedoor.cta')}
                  id="login-fakedoor-button">
                  {t('login.fakedoor.cta')}
                </GlButton>
              )}
              placement="top">
              <h3>{t('login.fakedoor.tooltip.title')}</h3>
              <p>{t('login.fakedoor.tooltip.body')}</p>
              <GlLink href={FAKEDOOR_SURVEY_URL} target="_blank">
                {t('login.fakedoor.tooltip.link')}
              </GlLink>
            </GlTooltip>
          )}
          {showPersistentLoginCheckbox && (
            <PersistentLoginCheckbox
              id="persistentLogin"
              className="gl-form-item gl-vspace-bpall-small"
              aria-label={t('persistent.login.label')}
              {...registerField('persistentLogin')}
            />
          )}
        </GlForm>
      )}
      <Consents {...mfProps} implicit scenarioType="login" />
      {generalError && <p className={styles['error']}>{generalError}</p>}
      {!isSocialMethodEnabled && (
        <GlButton
          variant="tertiary"
          className={styles['tertiary-link']}
          onClick={onPasswordReset}
          id="login-forgot-password-button">
          {t('password.forgot')}
        </GlButton>
      )}
      {isKmcFlowRequired && <KmcButton />}
    </FormContainer>
  )
}
